
import { useState, useEffect } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';

export const AndroidAppPromoPopup = () => {
  const [open, setOpen] = useState(false);
  const [lastDismissed, setLastDismissed] = useLocalStorage('android-app-promo-dismissed', '');
  
  useEffect(() => {
    // Check if popup should be shown
    const today = new Date().toDateString();
    const shouldShow = lastDismissed !== today;
    
    if (shouldShow) {
      // Show popup after a short delay for better UX
      const timer = setTimeout(() => {
        setOpen(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [lastDismissed]);

  const handleDismiss = () => {
    const today = new Date().toDateString();
    setLastDismissed(today);
    setOpen(false);
  };

  const handleDownload = () => {
    window.open('https://play.google.com/store/apps/details?id=com.edaptica.app', '_blank');
    handleDismiss();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md mx-auto p-0 bg-white border-0 shadow-2xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="relative p-8 rounded-2xl bg-gradient-to-br from-white to-blue-50/30"
        >
          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>

          {/* Content */}
          <div className="text-center">
            {/* App Icon */}
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg p-2">
                <img 
                  src="/lovable-uploads/35b65968-611d-4019-8203-f74bef63e388.png" 
                  alt="Edaptica App" 
                  className="w-12 h-12 object-contain"
                />
              </div>
            </div>

            {/* Headline */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              🎉 Get 7 Days Free on the Edaptica App!
            </h2>

            {/* Description */}
            <p className="text-gray-600 mb-8 leading-relaxed">
              Download our Android app now and unlock 7 days Free Access – completely free! No card needed.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={handleDownload}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                size="lg"
              >
                Download App
              </Button>
              
              <Button 
                onClick={handleDismiss}
                variant="ghost"
                className="w-full text-gray-500 hover:text-gray-700 py-2"
              >
                Maybe Later
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="mt-6 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Available on Google Play Store
              </p>
            </div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};
